/**
 * QuickMovementDialog — one-click "+ Movement" shortcut from a Cash
 * Account card. iter-208 · Fase B migration to useZodForm.
 *
 * The income/expense type pills are allowlisted raw HTML buttons —
 * they are a 2-state segmented toggle (not a CTA), no Button variant
 * matches that shape.
 */
import { useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { movementsService } from "../services/transactionsService";
import { tx } from "../i18n";
import { useLang } from "../contexts/LangContext";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Save } from "lucide-react";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "./form-ux";

export function QuickMovementDialog({ account, open, onOpenChange }) {
  const { lang } = useLang();
  const qc = useQueryClient();

  const Schema = z.object({
    type: z.enum(["income", "expense"]),
    amount: z.string().refine(
      (v) => {
        const n = parseFloat(String(v).replace(",", "."));
        return !Number.isNaN(n) && n > 0;
      },
      { message: tx(lang, "Invalid amount", "Monto inválido", "Valor inválido") },
    ),
    description: z.string().trim().min(1, tx(lang, "Description required", "Descripción requerida", "Descrição obrigatória")),
    date: z.string().min(1),
  });

  const todayIso = () => new Date().toISOString().slice(0, 10);

  const form = useZodForm(Schema, {
    type: "expense",
    amount: "",
    description: "",
    date: todayIso(),
  }, { formId: "quick-movement" });

  // Reset form whenever the dialog opens with a (possibly) different
  // account so stale state from a previous entry doesn't leak.
  useEffect(() => {
    if (open) {
      form.reset({
        type: "expense",
        amount: "",
        description: "",
        date: todayIso(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account?.id]);

  const create = useMutation({
    mutationFn: async (body) => movementsService.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["movements"] });
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["cash-accounts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(tx(lang, "Movement saved", "Movimiento guardado", "Movimentação salva"));
      onOpenChange(false);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail || tx(lang, "Could not save", "No se pudo guardar", "Não foi possível salvar"));
    },
  });

  const submit = form.handleSubmit(async (values) => {
    const n = parseFloat(String(values.amount).replace(",", "."));
    await create.mutateAsync({
      type: values.type,
      amount: n,
      original_currency: account?.currency || null,
      description: values.description.trim(),
      date: values.date,
      account_id: account?.id,
    });
  });

  if (!account) return null;
  const ccyLabel = account.currency || "USD";
  const type = form.values.type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-movement-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {tx(lang, "Quick movement", "Movimiento rápido", "Movimento rápido")}
          </DialogTitle>
          <DialogDescription>
            {tx(lang, "Booked against", "Contra la cuenta", "Lançado contra")}{" "}
            <span className="font-semibold">{account.name}</span>{" "}
            <span className="text-slate-400">({ccyLabel})</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" noValidate>
          {/* Type toggle — pill-style for fast tap. */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              {tx(lang, "Type", "Tipo", "Tipo")}
            </Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {/* design-system: ALLOW segmented-toggle pill (2-state, no Button variant matches) */}
              <button /* design-system: ALLOW legacy-todo */
                type="button"
                data-testid="quick-movement-type-income"
                onClick={() => form.setValue("type", "income")}
                className={`h-11 rounded-xl border text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  type === "income"
                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                    : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                }`}
              >
                <ArrowDown className="h-4 w-4" />
                {tx(lang, "Income", "Ingreso", "Receita")}
              </button>
              {/* design-system: ALLOW segmented-toggle pill (2-state, no Button variant matches) */}
              <button /* design-system: ALLOW legacy-todo */
                type="button"
                data-testid="quick-movement-type-expense"
                onClick={() => form.setValue("type", "expense")}
                className={`h-11 rounded-xl border text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  type === "expense"
                    ? "bg-red-50 border-red-300 text-red-700"
                    : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                }`}
              >
                <ArrowUp className="h-4 w-4" />
                {tx(lang, "Expense", "Gasto", "Despesa")}
              </button>
            </div>
          </div>

          <FormField
            label={<>{tx(lang, "Amount", "Monto", "Valor")} <span className="text-slate-400 normal-case">({ccyLabel})</span></>}
            name="amount"
            required
            error={form.errors.amount && (form.touched.amount || form.submitCount > 0) ? form.errors.amount : undefined}
          >
            <FormField.Input
              type="number"
              inputMode="decimal"
              step="0.01"
              autoFocus
              placeholder="0.00"
              data-testid="quick-movement-amount"
              className="h-11 text-base"
              {...form.getFieldProps("amount")}
            />
          </FormField>

          <FormField
            label={tx(lang, "Description", "Descripción", "Descrição")}
            name="description"
            required
            error={form.errors.description && (form.touched.description || form.submitCount > 0) ? form.errors.description : undefined}
          >
            <FormField.Input
              maxLength={140}
              placeholder={tx(lang, "e.g. coffee, salary…", "ej. café, sueldo…", "ex. café, salário…")}
              data-testid="quick-movement-description"
              className="h-11"
              {...form.getFieldProps("description")}
            />
          </FormField>

          <FormField label={tx(lang, "Date", "Fecha", "Data")} name="date">
            <FormField.Input
              type="date"
              data-testid="quick-movement-date"
              className="h-11"
              {...form.getFieldProps("date")}
            />
          </FormField>

          <DialogFooter className="gap-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="quick-movement-cancel">
              {tx(lang, "Cancel", "Cancelar", "Cancelar")}
            </Button>
            <SubmitButton
              submitting={form.submitting || create.isPending}
              loadingText={tx(lang, "Saving…", "Guardando…", "Salvando…")}
              testid="quick-movement-submit"
              className={type === "income"
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "bg-red-600 hover:bg-red-700 text-white"}
            >
              <Save className="h-4 w-4" />
              {tx(lang, "Save", "Guardar", "Salvar")}
            </SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
